import React, { useRef, useEffect, useState } from 'react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './VegetaisMovimentacao.css';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Formata número removendo zeros desnecessários ─────────────────────────────
const formatarNumero = (value) => {
    if (value === null || value === undefined) return '0';
    const numericValue = Number(value);
    if (isNaN(numericValue)) return String(value) || '0';

    const aux = Math.round(numericValue * 1000) / 1000;
    if (Number.isInteger(aux)) return numericValue;
    if (Number.isInteger(Math.round(aux * 10) / 10)) return Number(numericValue.toFixed(1));
    if (Number.isInteger(Math.round(aux * 100) / 100)) return Number(numericValue.toFixed(2));
    return Number(numericValue.toFixed(3));
};

// ── Renderer padrão para compra / estoque ────────────────────────────────────
const DefaultCellRenderer = (params) => {
    if (params.value === null || params.value === undefined) return '0';
    return formatarNumero(params.value);
};

// ── Renderer de Saída: amarelo + tooltip nativo se houver perda ───────────────
const SaidaCellRenderer = (veg) => (params) => {
    // 1. Pegamos o índice da linha atual
    const rowIndex = params.node.rowIndex;
    const api = params.api;
    
    // 2. Tentamos pegar os dados da próxima linha (dia seguinte)
    const nextRowNode = api.getDisplayedRowAtIndex(rowIndex - 1);
    const nextRowData = nextRowNode ? nextRowNode.data : null;

    // 3. A perda que aconteceu no dia 07 está na linha do dia 07
    // Se esta linha é dia 06, olhamos para a perda da rowData do dia 07
    const perdaValue = nextRowData ? nextRowData[`perda_${veg}`] : null;
    
    const temPerda = perdaValue !== null && perdaValue !== undefined && Number(perdaValue) > 0;

    const style = {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 4px',
        ...(temPerda
            ? {
                backgroundColor: '#fff3cd',
                color: '#856404',
                fontWeight: '600',
                borderRadius: '2px',
                cursor: 'help',
              }
            : {}),
    };

    const tooltipText = temPerda
        ? `Perda registrada no dia seguinte: ${formatarNumero(perdaValue)} kg`
        : undefined;

    return (
        <div style={style} title={tooltipText}>
            {formatarNumero(params.value)}
            {temPerda && (
                <span style={{ marginLeft: 4, fontSize: '0.7em', opacity: 0.8 }}>⚠</span>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const VegetaisMovimentacao = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [rowData, setRowData] = useState([]);
    const [error, setError] = useState('');
    const [vegetais, setVegetais] = useState([]);
    const gridRef = useRef();

    const { authenticatedFetch, logout } = useAuth();

    const fetchVegetaisData = async () => {
        setIsLoading(true);
        setRowData([]);
        setError('');

        try {
            const response = await authenticatedFetch('' + API_BASE_URL + '/api/vegetais-movimentacao/', {
                method: 'GET',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Erro ao buscar dados.');
            }

            const data = await response.json();
            console.log('Dados recebidos da API:', data);

            if (data.length > 0) {
                const firstRow = data[0];
                const vegetaisEncontrados = new Set();

                Object.keys(firstRow).forEach(key => {
                    if (key.startsWith('compra_')) {
                        vegetaisEncontrados.add(key.replace('compra_', ''));
                    }
                });

                setVegetais([...vegetaisEncontrados]);
            }

            setRowData(data);

        } catch (err) {
            if (err.message.includes('Token')) {
                logout();
            } else {
                alert('Erro ao buscar dados: ' + err.message);
                setError(err.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchVegetaisData();
    }, []);

    // ── Definições de coluna dinâmicas ──────────────────────────────────────
    const columnDefs = [
        {
            headerName: 'Data',
            field: 'data',
            cellClass: 'header-data-col',
            pinned: 'left',
            maxWidth: 90,
            minWidth: 90,
            suppressMenu: true,
            sortable: false,
            cellStyle: {
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
        },
        {
            headerName: 'Compra',
            children: vegetais.map((veg) => ({
                headerName: veg.charAt(0).toUpperCase() + veg.slice(1),
                field: `compra_${veg}`,
                cellClass: 'vegetable-cell',
                suppressMenu: true,
                sortable: false,
                maxWidth: 70,
                minWidth: 70,
                cellRenderer: DefaultCellRenderer,
            })),
        },
        {
            headerName: 'Estoque',
            children: vegetais.map((veg) => ({
                headerName: veg.charAt(0).toUpperCase() + veg.slice(1),
                field: `estoque_${veg}`,
                cellClass: 'vegetable-cell',
                suppressMenu: true,
                sortable: false,
                maxWidth: 70,
                minWidth: 70,
                cellRenderer: DefaultCellRenderer,
            })),
        },
        {
            headerName: 'Saída',
            children: vegetais.map((veg) => ({
                headerName: veg.charAt(0).toUpperCase() + veg.slice(1),
                field: `saida_${veg}`,
                cellClass: 'vegetable-cell',
                suppressMenu: true,
                sortable: false,
                maxWidth: 150,
                minWidth: 70,
                // ── Renderer com destaque de perda ─────────────────────────
                cellRenderer: SaidaCellRenderer(veg),
                // perda_${veg} está no rowData mas não precisa de coluna visível
            })),
        },
    ];

    const defaultColDef = {
        resizable: true,
        sortable: false,
        filter: false,
        flex: 1,
        suppressMenu: true,
    };

    useEffect(() => {
        if (gridRef.current && gridRef.current.api && rowData.length > 0) {
            setTimeout(() => {
                gridRef.current.api.sizeColumnsToFit();
            }, 100);
        }
    }, [rowData]);

    if (isLoading) return <div>Carregando...</div>;
    if (error) return <div>Erro: {error}</div>;

    return (
        <>
            {/* Legenda */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0 4px 0' }}>
                <div style={{
                    width: 14, height: 14,
                    backgroundColor: '#fff3cd',
                    border: '1px solid #856404',
                    borderRadius: 2,
                }} />
                <span style={{ fontSize: '0.78rem', color: '#555' }}>
                    Saída com perda registrada — passe o mouse para ver o valor
                </span>
            </div>

            <div className="ag-theme-custom" style={{ width: '1140px', height: '500px' }}>
                <br />
                <AgGridReact
                    theme="legacy"
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    suppressHorizontalScroll={false}
                    pagination={false}
                    suppressMenuHide={true}
                    onFirstDataRendered={(params) => {
                        params.api.sizeColumnsToFit();
                    }}
                />
            </div>
        </>
    );
};

export default VegetaisMovimentacao;